import { Dialog } from "@mui/material"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useFirebasePhotos } from "../../components/useFirebasePhotos"
import { isPhoneDevice } from "../../helper/device"
import { convertToDateJakartaTime, toIndoFormat } from "../../helper/helper"
import { openWhatsApp } from "../../helper/whatsapp"
import pempemMark from "../../rsc/ic_pempem.png"
import { CloseIcon, DownloadIcon, PhoneIcon, WhatsAppIcon, ZoomIcon } from "./TodayDeliveryIcons"
import { applyPhotoPositions, COORDINATE_SOURCE_LABEL, DeliveryRow, formatLatLng } from "./todayDeliveryModel"
import { watermarkPhoto } from "./watermarkPhoto"

type Props = {
    row: DeliveryRow
    millCoords?: [number, number]
    isOpen: boolean
    onClose: () => void
}

const NO_VALUE = '—'
const CAPTURED_PATTERN = 'DD MMM YYYY HH:mm'

const saveBlob = (blob: Blob, fileName: string) => {
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(objectUrl)
}

const Fact: React.FC<{ label: string, value: string }> = ({ label, value }) => (
    <div className="photo-dialog__fact">
        <span className="photo-dialog__fact-label">{label}</span>
        <span className="photo-dialog__fact-value">{value}</span>
    </div>
)

export const DeliveryPhotoDialog: React.FC<Props> = ({ row: listRow, millCoords, isOpen, onClose }) => {
    const { t } = useTranslation()

    const photoUri = listRow.photoKind === 'truck' ? listRow.photoUri : undefined
    const photos = useFirebasePhotos(photoUri ? [photoUri] : [])
    const photo = photoUri ? photos.get(photoUri) : undefined
    const isResolving = photoUri !== undefined && photo === undefined
    // Download and share only ever carry the truck photo, so both wait for it.
    const canShare = Boolean(photo?.url)

    // The truck photo's GPS only resolves here, so the position is redone once
    // it lands; the list keeps whatever rung it had.
    const row = useMemo(
        () => photoUri
            ? applyPhotoPositions([{ ...listRow, photoUri, photoKind: 'truck' }], () => photo?.coordinates, millCoords)[0]
            : listRow,
        [listRow, photoUri, photo, millCoords]
    )

    const photoLabel = t('truck_photo')

    const captured = photo?.capturedAt
        ? `${convertToDateJakartaTime(photo.capturedAt, CAPTURED_PATTERN)} WIB`
        : undefined
    const coordinates = row.coordinates ? formatLatLng(row.coordinates) : undefined
    const distance = row.distanceKm === undefined ? undefined : `${row.distanceKm.toFixed(1)} ${t('unit_km')}`
    const place = [row.village ?? row.supplierName, row.area].filter(Boolean).join(' · ')
    const fileName = `delivery-${row.contractId}.jpg`

    /** Stamped copy of the photo, prepared on open so a share click needs no await. */
    const [stamped, setStamped] = useState<Blob | undefined>(undefined)

    useEffect(() => {
        if (!isOpen || !photo?.url) return
        let cancelled = false

        watermarkPhoto(photo.url, {
            copy: t('pempem_verified'),
            coordinates,
            distance: distance && `${t('from_mill')}: ${distance}`,
            place: place || undefined,
            timestamp: captured,
        })
            .then(blob => { if (!cancelled) setStamped(blob) })
            // Tainted canvas (the bucket has no CORS rule for this host) or a
            // load failure: both buttons fall back to the unstamped original.
            .catch(() => { if (!cancelled) setStamped(undefined) })

        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, photo?.url, coordinates, distance, place, captured])

    const shareText = distance
        ? t('whatsapp_share_message', { distance, area: place, link: window.location.href })
        : t('whatsapp_share_message_no_distance', { area: place, link: window.location.href })

    const onDownload = async () => {
        if (stamped) return saveBlob(stamped, fileName)
        try {
            saveBlob(await (await fetch(photo.url)).blob(), fileName)
        } catch {
            // Cross-origin download attributes are ignored, so open it instead.
            window.open(photo.url, '_blank', 'noopener')
        }
    }

    /**
     * wa.me carries text only, so the stamped photo can only travel through the
     * OS share sheet — which reaches WhatsApp on a phone. On desktop, send the
     * message and leave the photo to the download button beside it.
     */
    const onShare = async () => {
        const file = stamped && new File([stamped], fileName, { type: 'image/jpeg' })

        if (isPhoneDevice() && file && (navigator as any).canShare?.({ files: [file] })) {
            try {
                await (navigator as any).share({ files: [file], text: shareText })
                return
            } catch (error) {
                // A dismissed share sheet is a choice, not a failure — do not
                // fall through and open WhatsApp behind the user.
                if ((error as Error)?.name === 'AbortError') return
            }
        }

        openWhatsApp(shareText)
    }

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth={false} className="photo-dialog">
            <div className="photo-dialog__panel">
                <header className="photo-dialog__head">
                    <div className="photo-dialog__headings">
                        <span className="photo-dialog__kicker">
                            {photoLabel.toUpperCase()} · {t('contract_id').toUpperCase()} {row.contractId}
                        </span>
                        <span className="photo-dialog__title">{row.village ?? row.supplierName ?? NO_VALUE}</span>
                        <span className="photo-dialog__place">{row.area ?? t('area_not_resolved')}</span>
                    </div>
                    <button type="button" className="photo-dialog__close" onClick={onClose} aria-label={t('close')}>
                        <CloseIcon />
                    </button>
                </header>

                <div className="photo-dialog__body">
                    <div className="photo-dialog__note">
                        <PhoneIcon color="var(--color-primary)" />
                        <span>{t('photo_provenance_truck')}</span>
                    </div>

                    <div className="photo-dialog__frame" title={t('tap_to_zoom')}>
                        {photo?.url
                            ? <img src={photo.url} alt={photoLabel} />
                            : <div className="photo-dialog__frame-empty">{t(isResolving ? 'loading' : photoUri ? 'photo_unavailable' : 'no_truck_photo')}</div>}

                        {photo?.url && (
                            <>
                                <a className="photo-dialog__zoom" href={photo.url} target="_blank" rel="noopener noreferrer">
                                    <ZoomIcon /> {t('zoom').toUpperCase()}
                                </a>

                                <span className="photo-dialog__verified">
                                    <img src={pempemMark} alt="" />
                                    {t('pempem_verified').toUpperCase()}
                                </span>

                                {/* Mirrors the stamp burned into the downloaded file. */}
                                {(coordinates || captured) && (
                                    <span className="photo-dialog__stamp">
                                        {coordinates && <span className="photo-dialog__stamp-coords">{coordinates}</span>}
                                        {distance && (
                                            <span className="photo-dialog__stamp-distance">
                                                {t('from_mill')}: {distance}
                                            </span>
                                        )}
                                        {place && <span className="photo-dialog__stamp-place">{place}</span>}
                                        {captured && <span className="photo-dialog__stamp-time">{captured}</span>}
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="photo-dialog__facts">
                    <Fact
                        label={row.coordinateSource
                            ? `${t('coordinates')} · ${t(COORDINATE_SOURCE_LABEL[row.coordinateSource])}`.toUpperCase()
                            : t('coordinates').toUpperCase()}
                        value={coordinates ?? NO_VALUE}
                    />
                    <Fact label={t('from_mill').toUpperCase()} value={distance ?? NO_VALUE} />
                    <Fact
                        label={t('price').toUpperCase()}
                        value={row.pricePerKg == null ? NO_VALUE : `Rp ${toIndoFormat(row.pricePerKg)} /kg`}
                    />
                    <Fact label={t('captured').toUpperCase()} value={captured ?? NO_VALUE} />
                </div>

                <footer className="photo-dialog__actions">
                    <button type="button" className="photo-dialog__download" disabled={!canShare} onClick={onDownload}>
                        <DownloadIcon color="var(--color-primary)" />
                        {t('download_photo')}
                    </button>

                    <button type="button" className="photo-dialog__share" disabled={!canShare} onClick={onShare}>
                        <WhatsAppIcon />
                        {t('share_on_whatsapp')}
                    </button>
                </footer>
            </div>
        </Dialog>
    )
}
